import {
  ButtonDS,
  DialogModalDS,
  SearchBarDS,
  Spacer,
  TableDS,
  Tag,
  TextCapitalized,
  triggerToastDS,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { energyGroups } from "@qivia/schemas-shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  driversListAsync,
  selectDriversList,
  selectDriversListStatus,
} from "../../drivers/driversSlice";
import { selectCompany } from "../../homeSlice";
import { HomeChargingVehicleRow } from "../electricVehiclesAPI";
import {
  addHomeChargingVehiclesAsync,
  electricVehiclesSlice,
  selectAddHomeChargingVehiclesStatus,
  selectElectricVehicles,
  selectHomeChargingEligibleVehicles,
} from "../electricVehiclesSlice";
import { getHomeChargingAdditionRefreshDelay } from "../lib";
import "../../../../i18n/i18n";

const modalName = "electric_charging.electric_vehicles.modal";

export const HomeChargingVehiclesModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const { t } = useTranslation();
  const { visible, onCloseModal } = props;
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const drivers = useAppSelector(selectDriversList);
  const driversListStatus = useAppSelector(selectDriversListStatus);
  const eligibleVehicles = useAppSelector(selectHomeChargingEligibleVehicles);
  const electricVehicles = useAppSelector(selectElectricVehicles);
  const addHomeChargingVehiclesStatus = useAppSelector(
    selectAddHomeChargingVehiclesStatus,
  );
  const [selectedRows, setSelectedRows] = useState<HomeChargingVehicleRow[]>(
    [],
  );
  const [listDisplayed, setListDisplayed] = useState<HomeChargingVehicleRow[]>(
    [],
  );

  useEffect(() => {
    if (company && driversListStatus === "idle") {
      void dispatch(driversListAsync(company.uuid));
    }
  }, [company, dispatch, driversListStatus]);

  const vehicleRows = useMemo<HomeChargingVehicleRow[]>(
    () =>
      eligibleVehicles
        .filter(
          (vehicle) =>
            !electricVehicles.some(
              (electricVehicle) =>
                electricVehicle.uuid === vehicle.uuid &&
                electricVehicle.status !== "DEACTIVATED",
            ),
        )
        .map((vehicle) => ({
          registrationNumber: vehicle.registrationNumber,
          driverName:
            drivers.find((driver) => driver.uuid === vehicle.driverUuid)
              ?.name ?? null,
          energyType: vehicle.energyType,
        }))
        .sort((a, b) =>
          a.registrationNumber.localeCompare(b.registrationNumber),
        ),
    [eligibleVehicles, electricVehicles, drivers],
  );

  useEffect(() => {
    if (visible) {
      setSelectedRows([]);
      setListDisplayed(vehicleRows);
    }
  }, [visible, vehicleRows]);

  const onSubmit = useCallback(() => {
    if (selectedRows.length === 0) {
      return;
    }
    const selectedRegistrationNumbers = selectedRows.map(
      (row) => row.registrationNumber,
    );
    const vehiclesToConnect = eligibleVehicles
      .filter((vehicle) =>
        selectedRegistrationNumbers.includes(vehicle.registrationNumber),
      )
      .map((vehicle) => ({ uuid: vehicle.uuid }));
    void dispatch(addHomeChargingVehiclesAsync(vehiclesToConnect));
  }, [dispatch, eligibleVehicles, selectedRows]);

  useEffect(() => {
    if (addHomeChargingVehiclesStatus === "success") {
      triggerToastDS(t(`${modalName}.success`), "SUCCESS");
      dispatch(
        electricVehiclesSlice.actions.resetAddHomeChargingVehiclesStatus(),
      );
      dispatch(electricVehiclesSlice.actions.resetElectricVehiclesStatus());
      setTimeout(
        () =>
          dispatch(electricVehiclesSlice.actions.resetElectricVehiclesStatus()),
        getHomeChargingAdditionRefreshDelay(selectedRows.length),
      );
      dispatch(
        electricVehiclesSlice.actions.resetHomeChargingEligibleVehiclesStatus(),
      );
      setSelectedRows([]);
      onCloseModal();
    } else if (addHomeChargingVehiclesStatus === "failed") {
      triggerToastDS(t(`${modalName}.error`), "ERROR");
      dispatch(
        electricVehiclesSlice.actions.resetAddHomeChargingVehiclesStatus(),
      );
    }
  }, [
    addHomeChargingVehiclesStatus,
    t,
    dispatch,
    onCloseModal,
    selectedRows.length,
  ]);

  const render =
    (row: HomeChargingVehicleRow) => (key: keyof HomeChargingVehicleRow) => {
      switch (key) {
        case "registrationNumber":
          return <StyledRow>{row.registrationNumber}</StyledRow>;
        case "driverName":
          return <StyledRow>{row.driverName ?? "-"}</StyledRow>;
        case "energyType": {
          const energy = energyGroups.find((group) =>
            group.acronymEnergies.some((acronym) => acronym === row.energyType),
          )?.energy;
          if (!energy) {
            return <StyledVehicleType>-</StyledVehicleType>;
          }
          return (
            <StyledVehicleType>
              <Tag
                text={t(`${modalName}.vehicleType.${energy}`)}
                backgroundColor={
                  colors["colors/surfaces/background/background_level1"]
                }
                textColor={colors["colors/text/darkGrey"]}
              />
            </StyledVehicleType>
          );
        }
      }
    };

  const headers = {
    registrationNumber: { text: "" },
    driverName: { text: "" },
    energyType: { text: "" },
  };

  return (
    <DialogModalDS
      visible={visible}
      width={44}
      onClose={onCloseModal}
      title={t(`${modalName}.title`) || ""}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t(`${modalName}.cancel`)}
            format={"fill"}
            buttonType={"secondary"}
            onClick={onCloseModal}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t(`${modalName}.validate`)}
            format={"fill"}
            buttonType={"primary"}
            onClick={onSubmit}
            disabled={
              selectedRows.length === 0 ||
              addHomeChargingVehiclesStatus === "processing"
            }
          />
        ),
      }}
    >
      <StyledModalContainer>
        <StyledDescription>
          <TextCapitalized>{t(`${modalName}.description`)}</TextCapitalized>
        </StyledDescription>
        <Spacer y={1} />
        <SearchBarDS<HomeChargingVehicleRow>
          fullWidth
          values={vehicleRows}
          setFilterValues={setListDisplayed}
          placeholder={t("home.searchbar") ?? ""}
          keysToTranslate={["energyType"]}
          findKeysToTranslate={(_key, value) =>
            `${modalName}.vehicleType.${
              energyGroups.find((group) =>
                group.acronymEnergies.some((acronym) => acronym === value),
              )?.energy
            }`
          }
        />
        <Spacer y={1} />
        <TableDS<keyof HomeChargingVehicleRow, HomeChargingVehicleRow>
          data={listDisplayed}
          headers={headers}
          render={render}
          height={25}
          rowsChecked={selectedRows}
          setRowsChecked={setSelectedRows}
          headerOverlayed={
            <StyledHeader>
              {t(`${modalName}.table.header`, { number: selectedRows.length })}
            </StyledHeader>
          }
          emptyContent={
            <StyledEmptyTable>
              <Spacer y={2} />
              <TextCapitalized>
                {vehicleRows.length === 0
                  ? t(`${modalName}.page.empty.title`)
                  : t(`${modalName}.page.emptySearch.text`)}
              </TextCapitalized>
              <Spacer y={2} />
            </StyledEmptyTable>
          }
        />
      </StyledModalContainer>
    </DialogModalDS>
  );
};

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledDescription = styled.div`
  ${typographies["Body/M"]};
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledVehicleType = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const StyledHeader = styled.div`
  display: flex;
  ${typographies["Header/H3"]};
  color: ${colors["colors/text/black"]};
  align-items: center;
`;

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
`;
